import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useAtom, useAtomValue } from 'jotai'
import {
  KBFlex,
  KBText,
  KBTypography,
  KBButton,
  KBProgress,
  KBIcon,
  KBTooltip,
} from '@/components/atoms'
import {
  useOnboardingStatus,
  useDismissOnboarding,
  OnboardingStep,
  OnboardingStepStatus,
  ONBOARDING_STEP_CONFIG,
} from '@/hooks/useOnboarding'
import { useLocationAdminFindMany } from '@/api'
import { isTrialAtom, trialDaysRemainingAtom } from '@/store/trial.atom'
import {
  hideOnboardingPanelAtom,
  collapseOnboardingPanelAtom,
} from '@/store/onboarding.atom'
import { useAnalytics } from '@/hooks/useAnalytics'
import { cn } from '@/utils/tailwind.helper'

/**
 * Step dependencies - some steps require previous steps to be completed first
 */
const STEP_DEPENDENCIES: Partial<Record<OnboardingStep, OnboardingStep[]>> = {
  [OnboardingStep.UPLOAD_FLOOR_PLAN]: [OnboardingStep.CREATE_LOCATION],
  [OnboardingStep.ADD_RESOURCES]: [OnboardingStep.CREATE_LOCATION],
  [OnboardingStep.MAKE_BOOKING]: [OnboardingStep.ADD_RESOURCES],
  [OnboardingStep.LINK_RESOURCE_CALENDAR]: [
    OnboardingStep.INSTALL_CALENDAR_APP,
  ],
}

/**
 * i18n keys for dependency messages
 */
const STEP_DEP_KEYS: Record<OnboardingStep, string> = {
  [OnboardingStep.CREATE_LOCATION]: 'onboarding.dep.createLocation',
  [OnboardingStep.UPLOAD_FLOOR_PLAN]: 'onboarding.dep.uploadFloorPlan',
  [OnboardingStep.ADD_RESOURCES]: 'onboarding.dep.addResources',
  [OnboardingStep.INVITE_TEAM]: 'onboarding.dep.inviteTeam',
  [OnboardingStep.MAKE_BOOKING]: 'onboarding.dep.makeBooking',
  [OnboardingStep.INSTALL_CALENDAR_APP]: 'onboarding.dep.installCalendarApp',
  [OnboardingStep.LINK_RESOURCE_CALENDAR]:
    'onboarding.dep.linkResourceCalendar',
}

export interface OnboardingPanelProps {
  className?: string
}

/**
 * OnboardingPanel - Stripe-style setup guide panel
 *
 * A floating panel on the right side that guides users through setup:
 * - Expanded: Shows all steps with checkmarks
 * - Collapsed: Shows progress bar + next step
 * - Can be dismissed (hidden for session)
 */
const OnboardingPanel = (props: OnboardingPanelProps) => {
  const { className = '' } = props
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { track } = useAnalytics()

  const isTrial = useAtomValue(isTrialAtom)
  const daysRemaining = useAtomValue(trialDaysRemainingAtom)

  const { data: status, isLoading } = useOnboardingStatus()
  const dismissMutation = useDismissOnboarding()

  // Fetch first location for floor plan step navigation
  const { data: locationsData } = useLocationAdminFindMany(
    { limit: 1 },
    { query: { enabled: isTrial } }
  )
  const firstLocationId = locationsData?.result?.[0]?.id

  // Use Jotai atoms for state management (shared with OnboardingBadge)
  const [isHidden, setIsHidden] = useAtom(hideOnboardingPanelAtom)
  const [isCollapsed, setIsCollapsed] = useAtom(collapseOnboardingPanelAtom)

  // Don't show if not in trial, dismissed via API, all complete, or temporarily hidden
  if (!isTrial || status?.isDismissed || status?.isComplete || isHidden) {
    return null
  }

  if (isLoading) {
    return null
  }

  const steps = Object.values(OnboardingStep)
  const completedSteps = status?.completedSteps ?? 0
  const totalSteps = status?.totalSteps ?? steps.length
  const progressPercent = status?.progressPercent ?? 0

  // Helper to check if a step's dependencies are met
  const isStepLocked = (step: OnboardingStep): boolean => {
    const dependencies = STEP_DEPENDENCIES[step]
    if (!dependencies) return false

    return dependencies.some((dep) => {
      const depStatus = status?.steps?.find(
        (s: OnboardingStepStatus) => s.key === dep
      )
      return !depStatus?.completed
    })
  }

  // Helper to get the lock reason message
  const getLockReason = (step: OnboardingStep): string | null => {
    const dependencies = STEP_DEPENDENCIES[step]
    if (!dependencies) return null

    const unmetDeps = dependencies.filter((dep) => {
      const depStatus = status?.steps?.find(
        (s: OnboardingStepStatus) => s.key === dep
      )
      return !depStatus?.completed
    })

    if (unmetDeps.length === 0) return null

    const depNames = unmetDeps
      .map((dep) => t(STEP_DEP_KEYS[dep]))
      .join(` ${t('common.and', 'and')} `)
    return t('onboarding.completeDependencyFirst', { steps: depNames })
  }

  // Find the next incomplete step that is not locked
  const nextStep = steps.find((step) => {
    const stepStatus = status?.steps?.find(
      (s: OnboardingStepStatus) => s.key === step
    )
    return !stepStatus?.completed && !isStepLocked(step)
  })
  const nextStepConfig = nextStep ? ONBOARDING_STEP_CONFIG[nextStep] : null

  const handleStepClick = (step: OnboardingStep) => {
    // Don't navigate if step is locked
    if (isStepLocked(step)) {
      return
    }

    const config = ONBOARDING_STEP_CONFIG[step]
    const stepStatus = status?.steps?.find(
      (s: OnboardingStepStatus) => s.key === step
    )
    track('onboarding_panel.step_clicked', {
      step,
      completed: stepStatus?.completed ?? false,
    })

    // Resolve dynamic placeholders in href
    let href = config.href
    if (href.includes(':locationId') && firstLocationId) {
      href = href.replace(':locationId', String(firstLocationId))
    }

    // Pass state for floor plan step to open Settings tab
    if (step === OnboardingStep.UPLOAD_FLOOR_PLAN) {
      navigate(href, { state: { isSettings: true } })
    } else {
      navigate(href)
    }
  }

  const handleToggleExpand = () => {
    const newCollapsed = !isCollapsed
    setIsCollapsed(newCollapsed)
    track('onboarding_panel.toggled', { expanded: !newCollapsed })
  }

  // Hide panel temporarily (can be reopened via badge)
  const handleHide = () => {
    track('onboarding_panel.hidden', {
      completed_steps: completedSteps,
      total_steps: totalSteps,
    })
    setIsHidden(true)
  }

  // Permanently dismiss via API (won't show again)
  const handlePermanentDismiss = () => {
    track('onboarding_panel.dismissed', {
      completed_steps: completedSteps,
      total_steps: totalSteps,
    })
    setIsHidden(true)
    dismissMutation.mutate()
  }

  const handleExpandToFull = () => {
    setIsCollapsed(false)
  }

  // Get step title for display using i18n keys
  const getStepTitle = (step: OnboardingStep) => {
    const config = ONBOARDING_STEP_CONFIG[step]
    return t(config.titleKey)
  }

  // Collapsed view - just progress bar and next step
  if (isCollapsed) {
    return (
      <div
        className={cn(
          'tw-fixed tw-bottom-6 tw-right-6 tw-z-[1000]',
          'tw-w-[320px] tw-rounded-lg tw-border tw-border-solid tw-border-gray-200',
          'tw-bg-white tw-shadow-lg',
          className
        )}
      >
        <div className="tw-p-4">
          <KBFlex justify="space-between" align="center" className="tw-mb-3">
            <KBText className="tw-font-semibold tw-text-gray-900">
              {t('onboarding.setupGuide')}
            </KBText>
            <KBFlex gap={4}>
              <KBButton
                type="text"
                size="small"
                icon={<KBIcon name="maximize-2" size={14} />}
                onClick={handleExpandToFull}
                className="tw-h-6 tw-w-6 tw-p-0 tw-text-gray-400 hover:tw-text-gray-600"
              />
              <KBButton
                type="text"
                size="small"
                icon={<KBIcon name="x" size={14} />}
                onClick={handleHide}
                className="tw-h-6 tw-w-6 tw-p-0 tw-text-gray-400 hover:tw-text-gray-600"
              />
            </KBFlex>
          </KBFlex>

          <KBProgress
            percent={progressPercent}
            size="small"
            showInfo={false}
            strokeColor="#6366f1"
            className="tw-mb-3"
          />

          {nextStep && nextStepConfig && (
            <KBFlex
              align="center"
              gap={8}
              className="tw-cursor-pointer tw-rounded-md tw-p-2 tw-transition-colors hover:tw-bg-gray-50"
              onClick={() => handleStepClick(nextStep)}
            >
              <KBText className="tw-text-sm tw-text-gray-500">
                {t('onboarding.next')}
              </KBText>
              <KBText className="tw-text-sm tw-font-medium tw-text-indigo-600">
                {getStepTitle(nextStep)}
              </KBText>
            </KBFlex>
          )}
        </div>
      </div>
    )
  }

  // Expanded view - full checklist
  return (
    <div
      className={cn(
        'tw-fixed tw-bottom-6 tw-right-6 tw-z-[1000]',
        'tw-max-h-[calc(100vh-100px)] tw-w-[360px] tw-overflow-hidden',
        'tw-rounded-lg tw-border tw-border-solid tw-border-gray-200',
        'tw-bg-white tw-shadow-xl',
        className
      )}
    >
      {/* Header */}
      <div className="tw-border-b tw-border-gray-100 tw-p-4">
        <KBFlex justify="space-between" align="center">
          <KBFlex align="center" gap={8}>
            <KBIcon name="rocket" size={20} className="tw-text-indigo-600" />
            <KBTypography.Text className="tw-text-base tw-font-semibold">
              {t('onboarding.setupGuide')}
            </KBTypography.Text>
          </KBFlex>
          <KBFlex gap={4}>
            <KBButton
              type="text"
              size="small"
              icon={<KBIcon name="minimize-2" size={14} />}
              onClick={handleToggleExpand}
              className="tw-h-6 tw-w-6 tw-p-0 tw-text-gray-400 hover:tw-text-gray-600"
            />
            <KBButton
              type="text"
              size="small"
              icon={<KBIcon name="x" size={14} />}
              onClick={handleHide}
              className="tw-h-6 tw-w-6 tw-p-0 tw-text-gray-400 hover:tw-text-gray-600"
            />
          </KBFlex>
        </KBFlex>

        {/* Progress */}
        <div className="tw-mt-3">
          <KBFlex justify="space-between" align="center" className="tw-mb-1">
            <KBText className="tw-text-xs tw-text-gray-500">
              {t('onboarding.progressCount', {
                completed: completedSteps,
                total: totalSteps,
              })}
            </KBText>
            {daysRemaining !== null && (
              <KBText className="tw-text-xs tw-text-gray-500">
                {t('onboarding.daysLeftInTrial', { days: daysRemaining })}
              </KBText>
            )}
          </KBFlex>
          <KBProgress
            percent={progressPercent}
            size="small"
            showInfo={false}
            strokeColor="#6366f1"
          />
        </div>
      </div>

      {/* Steps list */}
      <div className="tw-max-h-[400px] tw-overflow-y-auto tw-p-2">
        {steps.map((step, index) => {
          const config = ONBOARDING_STEP_CONFIG[step]
          const stepStatus = status?.steps?.find(
            (s: OnboardingStepStatus) => s.key === step
          )
          const isCompleted = stepStatus?.completed ?? false
          const isNext = step === nextStep
          const isLocked = isStepLocked(step)
          const lockReason = getLockReason(step)

          const stepContent = (
            <div
              key={step}
              className={cn(
                'tw-rounded-lg tw-p-3 tw-transition-all',
                isLocked
                  ? 'tw-cursor-not-allowed tw-opacity-50'
                  : 'tw-cursor-pointer',
                isCompleted
                  ? 'tw-bg-gray-50 hover:tw-bg-gray-100'
                  : isNext
                    ? 'tw-bg-indigo-50 hover:tw-bg-indigo-100'
                    : isLocked
                      ? 'tw-bg-gray-50'
                      : 'hover:tw-bg-gray-50'
              )}
              onClick={() => handleStepClick(step)}
            >
              <KBFlex align="start" gap={12}>
                {/* Step indicator */}
                <div
                  className={cn(
                    'tw-flex tw-h-6 tw-w-6 tw-flex-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-text-xs tw-font-medium',
                    isCompleted
                      ? 'tw-bg-green-500 tw-text-white'
                      : isNext
                        ? 'tw-bg-indigo-600 tw-text-white'
                        : isLocked
                          ? 'tw-border tw-border-gray-200 tw-bg-gray-100 tw-text-gray-400'
                          : 'tw-border tw-border-gray-300 tw-bg-white tw-text-gray-500'
                  )}
                >
                  {isCompleted ? (
                    <KBIcon name="check" size={14} />
                  ) : isLocked ? (
                    <KBIcon name="lock" size={12} />
                  ) : (
                    index + 1
                  )}
                </div>

                {/* Step content */}
                <div className="tw-min-w-0 tw-flex-1">
                  <KBText
                    className={cn(
                      'tw-block tw-text-sm tw-font-medium',
                      isCompleted
                        ? 'tw-text-gray-500 tw-line-through'
                        : isLocked
                          ? 'tw-text-gray-400'
                          : isNext
                            ? 'tw-text-indigo-700'
                            : 'tw-text-gray-700'
                    )}
                  >
                    {getStepTitle(step)}
                  </KBText>
                  {isNext && !isCompleted && !isLocked && (
                    <KBText className="tw-mt-0.5 tw-block tw-text-xs tw-text-indigo-600">
                      {t('onboarding.clickToStart')}
                    </KBText>
                  )}
                  {isLocked && lockReason && (
                    <KBText className="tw-mt-0.5 tw-block tw-text-xs tw-text-gray-400">
                      {lockReason}
                    </KBText>
                  )}
                </div>

                {/* Arrow or Lock icon */}
                {isLocked ? (
                  <KBIcon
                    name="lock"
                    size={14}
                    className="tw-flex-shrink-0 tw-text-gray-300"
                  />
                ) : (
                  <KBIcon
                    name="chevron-right"
                    size={16}
                    className={cn(
                      'tw-flex-shrink-0',
                      isCompleted ? 'tw-text-gray-300' : 'tw-text-gray-400'
                    )}
                  />
                )}
              </KBFlex>
            </div>
          )

          // Wrap locked steps with tooltip
          if (isLocked && lockReason) {
            return (
              <KBTooltip key={step} title={lockReason}>
                {stepContent}
              </KBTooltip>
            )
          }

          return stepContent
        })}
      </div>

      {/* Footer with skip option */}
      <div className="tw-border-t tw-border-gray-100 tw-p-3">
        <KBButton
          type="text"
          size="small"
          onClick={handlePermanentDismiss}
          className="tw-w-full tw-text-gray-500 hover:tw-text-gray-700"
        >
          {t('onboarding.skipSetupGuide')}
        </KBButton>
      </div>
    </div>
  )
}

export default OnboardingPanel
